import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah, KATEGORI, FRANCHISE_CLS } from "@/lib/api";
import { PageHeader, Badge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, ShoppingCart, Package } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 transition";

export default function POS() {
  const { isAdmin } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [sales, setSales] = useState([]);
  const [cart, setCart] = useState([]);
  const [q, setQ] = useState("");
  const [kategori, setKategori] = useState("all");
  const [metode, setMetode] = useState("cash");
  const [channel, setChannel] = useState("direct");
  const [diskon, setDiskon] = useState(0);
  const [pelanggan, setPelanggan] = useState("");

  const loadInventory = useCallback(async () => {
    const { data } = await api.get("/inventory");
    setInventory(data);
  }, []);

  const loadEvents = useCallback(async () => {
    const { data } = await api.get("/events");
    setEvents(data);
  }, []);

  const loadSales = useCallback(async () => {
    const params = selectedEvent ? { event_id: selectedEvent } : { store_only: true };
    const { data } = await api.get("/sales", { params });
    setSales(data);
  }, [selectedEvent]);

  useEffect(() => {
    loadInventory();
    loadEvents();
  }, [loadInventory, loadEvents]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const addToCart = (item) => {
    if (item.stok_qty <= 0) return toast.error("Stok habis");
    setCart((c) => {
      const ex = c.find((x) => x.inventory_id === item.id);
      if (ex) {
        if (ex.qty >= item.stok_qty) {
          toast.error("Melebihi stok tersedia");
          return c;
        }
        return c.map((x) => (x.inventory_id === item.id ? { ...x, qty: x.qty + 1 } : x));
      }
      return [...c, { inventory_id: item.id, nama_produk: item.nama_produk, qty: 1, harga_jual: item.harga_jual, harga_beli: item.harga_beli, max: item.stok_qty }];
    });
  };

  const updateQty = (invId, delta) =>
    setCart((c) =>
      c
        .map((x) => (x.inventory_id === invId ? { ...x, qty: Math.min(Math.max(x.qty + delta, 0), x.max) } : x))
        .filter((x) => x.qty > 0)
    );

  const setPrice = (invId, price) =>
    setCart((c) => c.map((x) => (x.inventory_id === invId ? { ...x, harga_jual: Number(price) } : x)));

  const subtotal = cart.reduce((a, b) => a + b.harga_jual * b.qty, 0);
  const total = subtotal - Number(diskon || 0);
  const scopeLabel = selectedEvent
    ? events.find((e) => e.id === selectedEvent)?.nama_event || "Event"
    : "Penjualan Toko (Umum)";

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    try {
      await api.post("/sales", {
        event_id: selectedEvent || null,
        channel: selectedEvent ? "event" : channel,
        items: cart.map((c) => ({ inventory_id: c.inventory_id, nama_produk: c.nama_produk, qty: c.qty, harga_jual: c.harga_jual, harga_beli: c.harga_beli })),
        metode_pembayaran: metode,
        diskon: Number(diskon || 0),
        nama_pelanggan: pelanggan,
      });
      toast.success(`Transaksi tercatat ke ${scopeLabel} • ${formatRupiah(total)}`);
      setCart([]);
      setDiskon(0);
      setPelanggan("");
      loadInventory();
      loadSales();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mencatat transaksi");
    }
  };

  const delSale = async (id) => {
    if (!window.confirm("Hapus transaksi ini? Stok akan dikembalikan.")) return;
    await api.delete(`/sales/${id}`);
    toast.success("Transaksi dihapus");
    loadInventory();
    loadSales();
  };

  const tabs = [
    { k: "all", label: "Semua" },
    { k: "sealed", label: "Sealed" },
    { k: "raw", label: "Raw" },
    { k: "slab", label: "Slab" },
  ];

  const filtered = inventory.filter(
    (i) =>
      i.nama_produk.toLowerCase().includes(q.toLowerCase()) &&
      (kategori === "all" || i.kategori === kategori)
  );

  return (
    <div>
      <PageHeader
        title="Kasir (POS)"
        subtitle="Penjualan"
        action={
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono uppercase tracking-wider text-slate-500 hidden sm:inline">Catat ke</span>
            <select
              data-testid="pos-scope-select"
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500 transition min-w-[220px]"
            >
              <option value="">Penjualan Toko (Umum)</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.nama_event}{ev.status === "active" ? " • Aktif" : ""}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Catalog */}
        <div className="lg:col-span-7 xl:col-span-8">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex gap-1.5">
              {tabs.map((t) => (
                <button
                  key={t.k}
                  data-testid={`pos-tab-${t.k}`}
                  onClick={() => setKategori(t.k)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${
                    kategori === t.k ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40" : "bg-slate-800/50 text-slate-400 border border-transparent hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input data-testid="pos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..." className={`${inputCls} pl-9`} />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Package className="w-10 h-10 text-slate-600 mb-3" />
              <p className="text-sm text-slate-500">Tidak ada produk. Tambahkan stok di halaman Stok Produk.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="pos-catalog">
              {filtered.map((it) => {
                const k = KATEGORI[it.kategori] || KATEGORI.sealed;
                const habis = it.stok_qty <= 0;
                return (
                  <button
                    key={it.id}
                    data-testid={`pos-add-${it.id}`}
                    onClick={() => addToCart(it)}
                    disabled={habis}
                    className="text-left rounded-xl border border-slate-800 bg-[#131822] p-3.5 hover:border-indigo-500/50 hover:bg-slate-800/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div className="flex gap-1 mb-2 flex-wrap">
                      <Badge className={k.cls}>{k.label.split(" ")[0]}</Badge>
                    </div>
                    <p className="text-sm font-semibold text-white leading-snug line-clamp-2 min-h-[2.5rem]">{it.nama_produk}</p>
                    <Badge className={`mt-1.5 ${FRANCHISE_CLS[it.tcg_franchise] || FRANCHISE_CLS.Lainnya}`}>{it.tcg_franchise}</Badge>
                    <div className="flex items-end justify-between mt-2.5 pt-2.5 border-t border-slate-800">
                      <span className="font-mono font-bold text-emerald-300 text-sm">{formatRupiah(it.harga_jual)}</span>
                      <span className={`font-mono text-xs ${habis ? "text-rose-400" : "text-slate-400"}`}>Stok {it.stok_qty}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Cart */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="rounded-xl border border-slate-800 bg-[#0F131C] p-5 lg:sticky lg:top-6 flex flex-col">
            <h3 className="font-heading font-bold text-white flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-indigo-400" /> Keranjang
            </h3>

            <div className="flex-1 space-y-2 max-h-[40vh] overflow-y-auto" data-testid="pos-cart">
              {cart.length === 0 ? (
                <p className="text-center text-sm text-slate-600 py-10">Keranjang kosong. Pilih produk di kiri.</p>
              ) : (
                cart.map((c) => (
                  <div key={c.inventory_id} className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-sm text-white flex-1">{c.nama_produk}</p>
                      <button onClick={() => updateQty(c.inventory_id, -c.qty)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(c.inventory_id, -1)} className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-white"><Minus className="w-3 h-3" /></button>
                        <span className="font-mono text-sm text-white w-6 text-center">{c.qty}</span>
                        <button onClick={() => updateQty(c.inventory_id, 1)} className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-white"><Plus className="w-3 h-3" /></button>
                      </div>
                      <input type="number" value={c.harga_jual} onChange={(e) => setPrice(c.inventory_id, e.target.value)} className="w-28 px-2 py-1 rounded bg-slate-950/60 border border-slate-700 text-white text-sm text-right font-mono focus:outline-none focus:border-indigo-500" />
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-slate-800 mt-4 pt-4 space-y-3">
              <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800">
                <span className="text-slate-500">Dicatat ke:</span>
                <span data-testid="pos-scope-label" className={`font-medium ${selectedEvent ? "text-emerald-300" : "text-indigo-300"}`}>{scopeLabel}</span>
              </div>
              {!selectedEvent && (
                <select value={channel} onChange={(e) => setChannel(e.target.value)} className={inputCls} data-testid="pos-channel">
                  <option value="direct">Direct / Toko</option>
                  <option value="tokopedia">Tokopedia</option>
                  <option value="shopee">Shopee</option>
                  <option value="live">Live Selling</option>
                </select>
              )}
              <input value={pelanggan} onChange={(e) => setPelanggan(e.target.value)} placeholder="Nama pelanggan (opsional)" className={inputCls} data-testid="pos-customer" />
              <div className="grid grid-cols-2 gap-3">
                <select value={metode} onChange={(e) => setMetode(e.target.value)} className={inputCls} data-testid="pos-payment">
                  <option value="cash">Cash</option>
                  <option value="qris">QRIS</option>
                  <option value="transfer">Transfer</option>
                </select>
                <input type="number" value={diskon} onChange={(e) => setDiskon(e.target.value)} placeholder="Diskon" className={inputCls} data-testid="pos-discount" />
              </div>
              <div className="flex justify-between text-sm text-slate-400"><span>Subtotal</span><span className="font-mono">{formatRupiah(subtotal)}</span></div>
              <div className="flex justify-between text-base font-bold text-white"><span>Total</span><span className="font-mono text-emerald-300">{formatRupiah(total)}</span></div>
              <button data-testid="checkout-button" onClick={checkout} className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition shadow-lg shadow-indigo-600/30">
                Bayar & Simpan
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent store sales */}
      <div className="mt-8 rounded-xl border border-slate-800 bg-[#131822] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800">
          <h3 className="font-heading font-semibold text-white">{selectedEvent ? `Transaksi Terakhir — ${scopeLabel}` : "Transaksi Toko Terakhir"}</h3>
        </div>
        {sales.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">{selectedEvent ? "Belum ada transaksi di event ini." : "Belum ada transaksi toko."}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="store-sales-table">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">Waktu</th>
                  <th className="text-left px-5 py-3">Item</th>
                  <th className="text-left px-5 py-3">Pelanggan</th>
                  <th className="text-left px-5 py-3">Bayar</th>
                  <th className="text-right px-5 py-3">Total</th>
                  {isAdmin && <th className="text-right px-5 py-3">Laba</th>}
                  {isAdmin && <th className="px-5 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {sales.map((s) => (
                  <tr key={s.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{new Date(s.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-5 py-3 text-slate-300">{s.items.map((i) => `${i.nama_produk} ×${i.qty}`).join(", ")}</td>
                    <td className="px-5 py-3 text-slate-400">{s.nama_pelanggan || "-"}</td>
                    <td className="px-5 py-3"><span className="text-xs uppercase font-mono text-slate-400">{s.metode_pembayaran}</span></td>
                    <td className="px-5 py-3 text-right font-mono text-white">{formatRupiah(s.total)}</td>
                    {isAdmin && <td className="px-5 py-3 text-right font-mono text-emerald-300">{formatRupiah(s.laba)}</td>}
                    {isAdmin && (
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => delSale(s.id)} data-testid={`delete-store-sale-${s.id}`} className="text-slate-500 hover:text-rose-400 transition"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
